import { useState, useEffect, useCallback } from 'react';
import { Calendar, Upload, Image, Quote, PartyPopper, AlertTriangle, Check, X, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDaily({ onLogout }) {
  const [activeTab, setActiveTab] = useState('calendar');
  const [calendarData, setCalendarData] = useState([]);
  const [bunnyPhotos, setBunnyPhotos] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Photo assignment modal
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  
  // Calendar navigation
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const token = localStorage.getItem('admin_token');

  const fetchCalendar = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/daily/calendar?days=35`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    }
  }, [token]);

  const fetchBunnyPhotos = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/daily/bunny-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBunnyPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch Bunny photos:', error);
    }
  }, [token]);

  const fetchQuotes = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/daily/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    }
  }, [token]);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/daily/holidays`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCalendar(), fetchBunnyPhotos(), fetchQuotes(), fetchHolidays()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCalendar, fetchBunnyPhotos, fetchQuotes, fetchHolidays]);

  // Upload photo to Bunny.net
  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${BACKEND_URL}/api/admin/daily/upload-photo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedUrls.push(response.data.url);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploadedUrls.length > 0) {
      toast.success(`Uploaded ${uploadedUrls.length} photo(s)`);
      fetchBunnyPhotos();
    }

    setUploading(false);
    e.target.value = '';
  };

  // Upload quotes file
  const handleQuotesUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/admin/daily/quotes/upload?mode=replace`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload quotes');
    }

    setUploading(false);
    e.target.value = '';
  };

  // Upload holidays file
  const handleHolidaysUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/admin/daily/holidays/upload?mode=replace`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message);
      fetchHolidays();
      fetchCalendar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload holidays');
    }

    setUploading(false);
    e.target.value = '';
  };

  // Assign photo to date
  const handleAssignPhoto = async () => {
    if (!selectedDate || !selectedPhoto) return;

    try {
      await axios.post(`${BACKEND_URL}/api/admin/daily/photos`, {
        date: selectedDate,
        image_url: selectedPhoto,
        caption: photoCaption || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Photo assigned to ${selectedDate}`);
      setShowPhotoModal(false);
      setSelectedDate(null);
      setSelectedPhoto(null);
      setPhotoCaption('');
      fetchCalendar();
    } catch (error) {
      toast.error('Failed to assign photo');
    }
  };

  // Remove photo from date
  const handleRemovePhoto = async (date) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/daily/photos/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo removed');
      fetchCalendar();
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  // Send daily email
  const handleSendEmail = async (date) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/daily/send-email?date=${date}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Add empty slots for days before the first day of the month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    // Add days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const calendarEntry = calendarData.find(c => c.date === dateStr);
      days.push({
        date: dateStr,
        day: d,
        ...calendarEntry
      });
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCalendarMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const openPhotoModal = (dateStr) => {
    setSelectedDate(dateStr);
    const existing = calendarData.find(c => c.date === dateStr);
    if (existing?.photo) {
      setSelectedPhoto(existing.photo.image_url);
      setPhotoCaption(existing.photo.caption || '');
    } else {
      setSelectedPhoto(null);
      setPhotoCaption('');
    }
    setShowPhotoModal(true);
  };

  if (loading) {
    return (
      <AdminLayout currentPage="daily" onLogout={onLogout}>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p>Loading Daily OBX...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="daily" onLogout={onLogout}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '10px' }}>Daily OBX</h1>
          <p style={{ color: '#666' }}>Manage daily content for paid subscribers</p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
          {[
            { id: 'calendar', label: 'Photo Calendar', icon: Calendar },
            { id: 'photos', label: 'Photo Library', icon: Image },
            { id: 'quotes', label: 'Quotes', icon: Quote },
            { id: 'holidays', label: 'Holidays', icon: PartyPopper }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: activeTab === tab.id ? '#1a1a1a' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#666',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div>
            {/* Calendar Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <button onClick={() => navigateMonth(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px' }}>
                <ChevronLeft size={24} />
              </button>
              <h2 style={{ fontSize: '20px', fontWeight: '600' }}>
                {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button onClick={() => navigateMonth(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px' }}>
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Day Headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '2px' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} style={{ textAlign: 'center', padding: '10px', fontWeight: '600', color: '#666', fontSize: '12px' }}>
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
              {getDaysInMonth(calendarMonth).map((day, index) => (
                <div
                  key={index}
                  onClick={() => day && openPhotoModal(day.date)}
                  style={{
                    minHeight: '100px',
                    background: day ? '#fff' : '#f5f5f5',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    padding: '8px',
                    cursor: day ? 'pointer' : 'default',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {day && (
                    <>
                      <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{day.day}</span>
                        {day.warnings?.length > 0 && (
                          <AlertTriangle size={14} color="#f59e0b" />
                        )}
                      </div>
                      
                      {day.photo ? (
                        <img 
                          src={day.photo.image_url} 
                          alt="" 
                          style={{ 
                            width: '100%', 
                            height: '50px', 
                            objectFit: 'cover', 
                            borderRadius: '4px',
                            marginBottom: '5px'
                          }} 
                        />
                      ) : (
                        <div style={{ 
                          width: '100%', 
                          height: '50px', 
                          background: '#f0f0f0', 
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '5px'
                        }}>
                          <Image size={16} color="#ccc" />
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {day.has_quote && <Check size={12} color="#22c55e" title="Has quote" />}
                        {day.has_holiday && <PartyPopper size={12} color="#8b5cf6" title="Has holiday" />}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Preview Section */}
            <div style={{ marginTop: '40px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px' }}>Next 7 Days Preview</h3>
              <div style={{ display: 'grid', gap: '10px' }}>
                {calendarData.slice(0, 7).map(day => (
                  <div 
                    key={day.date}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '15px', 
                      padding: '15px', 
                      background: '#fff', 
                      border: '1px solid #eee',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ width: '80px', flexShrink: 0 }}>
                      <div style={{ fontWeight: '600' }}>{day.day_name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{day.date}</div>
                    </div>
                    
                    {day.photo ? (
                      <img src={day.photo.image_url} alt="" style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                    ) : (
                      <div style={{ width: '60px', height: '40px', background: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={16} color="#ccc" />
                      </div>
                    )}
                    
                    <div style={{ flex: 1, fontSize: '13px' }}>
                      {day.quote ? (
                        <div style={{ color: '#333' }}>"{day.quote.text.substring(0, 50)}..."</div>
                      ) : (
                        <div style={{ color: '#999' }}>No quote</div>
                      )}
                    </div>
                    
                    <div style={{ fontSize: '13px', color: day.holiday ? '#8b5cf6' : '#999' }}>
                      {day.holiday ? day.holiday.name : 'No holiday'}
                    </div>
                    
                    {day.warnings?.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f59e0b', fontSize: '12px' }}>
                        <AlertTriangle size={14} />
                        {day.warnings.join(', ')}
                      </div>
                    )}
                    
                    {day.has_photo && day.has_quote && (
                      <Button size="sm" onClick={() => handleSendEmail(day.date)}>
                        <Send size={14} style={{ marginRight: '5px' }} />
                        Send
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Photo Library Tab */}
        {activeTab === 'photos' && (
          <div>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '10px 20px', 
                background: '#1a1a1a', 
                color: '#fff', 
                borderRadius: '6px', 
                cursor: 'pointer' 
              }}>
                <Upload size={18} />
                {uploading ? 'Uploading...' : 'Upload Photos'}
                <input 
                  type="file" 
                  accept="image/*" 
                  multiple 
                  onChange={handlePhotoUpload} 
                  style={{ display: 'none' }} 
                  disabled={uploading}
                />
              </label>
              <span style={{ color: '#666', fontSize: '14px' }}>{bunnyPhotos.length} photos in library</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px' }}>
              {bunnyPhotos.map((photo, index) => (
                <div 
                  key={index}
                  style={{ 
                    background: '#fff', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    border: '1px solid #eee'
                  }}
                >
                  <img src={photo.url} alt="" style={{ width: '100%', height: '100px', objectFit: 'cover' }} />
                  <div style={{ padding: '8px', fontSize: '11px', color: '#666', wordBreak: 'break-all' }}>
                    {photo.filename}
                  </div>
                </div>
              ))}
            </div>

            {bunnyPhotos.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
                <Image size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
                <p>No photos uploaded yet. Upload photos to assign them to dates.</p>
              </div>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '10px 20px', 
                background: '#1a1a1a', 
                color: '#fff', 
                borderRadius: '6px', 
                cursor: 'pointer' 
              }}>
                <Upload size={18} />
                {uploading ? 'Uploading...' : 'Upload Quotes File'}
                <input 
                  type="file" 
                  accept=".txt" 
                  onChange={handleQuotesUpload} 
                  style={{ display: 'none' }} 
                  disabled={uploading}
                />
              </label>
              <span style={{ color: '#666', fontSize: '14px' }}>{quotes.length} quotes loaded</span>
            </div>

            <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#666' }}>
              <strong>File format:</strong> One quote per line<br />
              Example: "Sandcastles wash away. Build them anyway." -Beach Bum Code 13
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              {quotes.map((quote, index) => (
                <div 
                  key={quote.id}
                  style={{ 
                    padding: '15px', 
                    background: '#fff', 
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                  }}
                >
                  <span style={{ 
                    background: '#1a1a1a', 
                    color: '#fff', 
                    width: '30px', 
                    height: '30px', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {quote.code_number}
                  </span>
                  <span style={{ fontStyle: 'italic' }}>"{quote.text}"</span>
                </div>
              ))}
            </div>

            {quotes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
                <Quote size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
                <p>No quotes uploaded yet. Upload a text file with Beach Bum Code quotes.</p>
              </div>
            )}
          </div>
        )}

        {/* Holidays Tab */}
        {activeTab === 'holidays' && (
          <div>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '10px 20px', 
                background: '#1a1a1a', 
                color: '#fff', 
                borderRadius: '6px', 
                cursor: 'pointer' 
              }}>
                <Upload size={18} />
                {uploading ? 'Uploading...' : 'Upload Holidays File'}
                <input 
                  type="file" 
                  accept=".txt" 
                  onChange={handleHolidaysUpload} 
                  style={{ display: 'none' }} 
                  disabled={uploading}
                />
              </label>
              <span style={{ color: '#666', fontSize: '14px' }}>{holidays.length} holidays loaded</span>
            </div>

            <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#666' }}>
              <strong>File format:</strong> MM/DD, Holiday Name, optional description<br />
              Example: 09/19, Talk Like a Pirate Day, Arrr matey!
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              {holidays.map((holiday) => (
                <div 
                  key={holiday.id}
                  style={{ 
                    padding: '15px', 
                    background: '#fff', 
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                  }}
                >
                  <span style={{ 
                    background: '#8b5cf6', 
                    color: '#fff', 
                    padding: '5px 10px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {holiday.date}
                  </span>
                  <div>
                    <div style={{ fontWeight: '500' }}>{holiday.name}</div>
                    {holiday.description && (
                      <div style={{ fontSize: '13px', color: '#666' }}>{holiday.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {holidays.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
                <PartyPopper size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
                <p>No holidays uploaded yet. Upload a text file with fun holidays.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Photo Assignment Modal */}
      {showPhotoModal && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowPhotoModal(false)}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Assign Photo for {selectedDate}</h3>
              <button onClick={() => setShowPhotoModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {selectedPhoto && (
                <div style={{ marginBottom: '20px' }}>
                  <img src={selectedPhoto} alt="" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }} />
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Caption (optional)</label>
                <input
                  type="text"
                  value={photoCaption}
                  onChange={e => setPhotoCaption(e.target.value)}
                  placeholder="Enter a caption for this photo..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Select a photo from library</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                  {bunnyPhotos.map((photo, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedPhoto(photo.url)}
                      style={{
                        cursor: 'pointer',
                        border: selectedPhoto === photo.url ? '3px solid #1a1a1a' : '1px solid #eee',
                        borderRadius: '6px',
                        overflow: 'hidden'
                      }}
                    >
                      <img src={photo.url} alt="" style={{ width: '100%', height: '70px', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <Button onClick={handleAssignPhoto} disabled={!selectedPhoto}>
                  <Check size={18} style={{ marginRight: '5px' }} />
                  Save Photo
                </Button>
                {calendarData.find(c => c.date === selectedDate)?.photo && (
                  <Button variant="outline" onClick={() => handleRemovePhoto(selectedDate)}>
                    <X size={18} style={{ marginRight: '5px' }} />
                    Remove Photo
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminDaily;
